import {
  CameraVideo,
  useCameraSelection,
  useCameras,
  type DestinationCamera,
} from '../../destination'
import {
  registerPrimitive,
  type PrimitiveProps,
} from '../../layout'
import { EmptyStateInner } from './EmptyState'

export type MultiViewVariant = 'grid' | 'split' | 'pictureInPicture' | 'focused'
export type MultiViewSelection = 'featured' | 'viewer-selected' | 'all-online' | 'explicit'

export interface MultiViewConfig {
  variant?: MultiViewVariant
  selection?: MultiViewSelection
  identities?: string[]
  limit?: number
}

function moveFirst(
  cameras: DestinationCamera[],
  identity: string | null,
): DestinationCamera[] {
  if (!identity) return cameras
  const index = cameras.findIndex((camera) => camera.identity === identity)
  if (index <= 0) return cameras
  return [cameras[index], ...cameras.slice(0, index), ...cameras.slice(index + 1)]
}

export function selectMultiViewCameras(
  cameras: DestinationCamera[],
  config: MultiViewConfig,
  selectedIdentity: string | null,
  featuredIdentity: string | null,
): DestinationCamera[] {
  const online = cameras.filter((camera) => camera.online)
  const policy = config.selection ?? 'all-online'
  let selected = online

  if (policy === 'explicit') {
    const identities = new Set(config.identities ?? [])
    selected = online.filter((camera) => identities.has(camera.identity))
  } else if (policy === 'featured') {
    selected = moveFirst(online, featuredIdentity)
  } else if (policy === 'viewer-selected') {
    selected = moveFirst(online, selectedIdentity)
  }

  const limit = Math.max(2, Math.min(16, config.limit ?? 4))
  return selected.slice(0, limit)
}

function CameraTile({
  camera,
  selected,
  onSelect,
  className = '',
}: {
  camera: DestinationCamera
  selected: boolean
  onSelect: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      className={`lv-multiview-tile${selected ? ' lv-multiview-tile-selected' : ''}${className ? ` ${className}` : ''}`}
      aria-label={`Watch ${camera.name}`}
      aria-pressed={selected}
      title={camera.name}
      onClick={onSelect}
    >
      <CameraVideo identity={camera.identity} className="lv-multiview-video" />
      <span className="lv-multiview-name">{camera.name}</span>
    </button>
  )
}

export function MultiView({ config }: PrimitiveProps) {
  const cameras = useCameras()
  const cameraSelection = useCameraSelection()
  const cfg = config as MultiViewConfig
  const variant = cfg.variant ?? 'grid'
  const selected = selectMultiViewCameras(
    cameras,
    cfg,
    cameraSelection.selectedIdentity,
    cameraSelection.producerFeaturedIdentity,
  )

  if (selected.length === 0) {
    return <EmptyStateInner state="awaiting-cameras" />
  }

  if (variant === 'pictureInPicture') {
    const [primary, secondary] = selected
    return (
      <div className="lv-multiview lv-multiview-pip">
        <CameraTile
          camera={primary}
          selected={cameraSelection.selectedIdentity === primary.identity}
          onSelect={() => cameraSelection.select(primary.identity)}
          className="lv-multiview-primary"
        />
        {secondary && (
          <CameraTile
            camera={secondary}
            selected={cameraSelection.selectedIdentity === secondary.identity}
            onSelect={() => cameraSelection.select(secondary.identity)}
            className="lv-multiview-inset"
          />
        )}
      </div>
    )
  }

  if (variant === 'focused') {
    const focused =
      selected.find((camera) => camera.identity === cameraSelection.selectedIdentity) ??
      selected[0]
    return (
      <div className="lv-multiview lv-multiview-focused">
        <CameraTile
          camera={focused}
          selected
          onSelect={() => cameraSelection.select(focused.identity)}
          className="lv-multiview-primary"
        />
        {selected.length > 1 && (
          <div className="lv-multiview-strip">
            {selected.map((camera) => (
              <CameraTile
                key={camera.identity}
                camera={camera}
                selected={camera.identity === focused.identity}
                onSelect={() => cameraSelection.select(camera.identity)}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  const visible = variant === 'split' ? selected.slice(0, 2) : selected
  return (
    <div
      className={`lv-multiview lv-multiview-${variant}`}
      data-count={visible.length}
    >
      {visible.map((camera) => (
        <CameraTile
          key={camera.identity}
          camera={camera}
          selected={cameraSelection.selectedIdentity === camera.identity}
          onSelect={() => cameraSelection.select(camera.identity)}
        />
      ))}
    </div>
  )
}

registerPrimitive('MultiView', MultiView)
